import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/hec/components/ui/accordion";

import { AccordionContext, useAccordionContext } from "./accordion-context";
import { defaultDetail, defaultItem, defaultTitle } from "./default-config";

// ---------------------------------------------------------------------------
// Accordion Builder block suite (SFCC-11747)
//
// Ported 1:1 from hec-composable's hec/builder/blocks/accordion/* so that
// authored Builder content referencing these exact component names continues
// to render on the Workers storefront:
//   - "Builder:Accordion"  (parent)
//   - "AccordionItem"
//   - "AccordionTitle"
//   - "AccordionDetail"
//
// The parent flattens its authored items, owns the open/closed state, and
// publishes per-item context (index, arrow direction, highlight, expansion
// predicate). Item / Title / Detail blocks consume that context and wrap the
// already-present Workers HEC accordion primitives in
// ~/hec/components/ui/accordion. Nested authored content is rendered in-place
// via the Builder <Blocks> renderer at the correct `path`, so the editor can
// select and edit individual nested blocks.
//
// All four are noWrap blocks → each spreads `attributes` on its root element
// (working-conventions.md §5).
// ---------------------------------------------------------------------------

type BlockAttributes = {
  className?: string;
  [key: string]: unknown;
};

const splitAttributes = (attributes?: BlockAttributes) => {
  const { className, ...rest } = attributes ?? {};
  return { className, restAttributes: rest };
};

// ---------------------------------------------------------------------------
// Parent: Builder:Accordion
// ---------------------------------------------------------------------------

interface AccordionItemTree {
  content: unknown[];
}

interface BuilderAccordionProps {
  items?: AccordionItemTree[];
  oneAtATime?: boolean;
  arrowDirection?: "right" | "down";
  highlightExpandedItem?: boolean;
  /** Injected by Builder via shouldReceiveBuilderProps — parent ID for <Blocks>. */
  builderBlock?: { id?: string };
  attributes?: BlockAttributes;
  "data-testid"?: string;
}

const BuilderAccordion = ({
  items = [],
  oneAtATime = false,
  arrowDirection = "right",
  highlightExpandedItem = false,
  builderBlock,
  attributes,
  "data-testid": dataTestId,
}: BuilderAccordionProps) => {
  const { className, restAttributes } = splitAttributes(attributes);

  const flatContent = useMemo(
    () => items.flatMap(item => item.content),
    [items],
  );
  const allItemsForEditMode = useMemo(
    () => flatContent.map((_, i) => `item-${i}`),
    [flatContent],
  );

  // In the Builder editor every item is forced open so editors can author all
  // nested content. On a live page the open set follows the authored mode.
  const [expandedItems, setExpandedItems] = useState<string[] | string>(() =>
    isEditing() ? allItemsForEditMode : oneAtATime ? "" : [],
  );

  useEffect(() => {
    if (isEditing()) {
      setExpandedItems(allItemsForEditMode);
    }
  }, [allItemsForEditMode]);

  const shouldExpandDetails = useCallback(
    (index: number) => {
      if (isEditing()) return true;
      if (oneAtATime) return expandedItems === `item-${index}`;
      return Array.isArray(expandedItems)
        ? expandedItems.includes(`item-${index}`)
        : false;
    },
    [expandedItems, oneAtATime],
  );

  if (flatContent.length === 0) return null;

  // Radix Accordion is a discriminated union on `type`; the value/onValueChange
  // pair differs between "single" (string) and "multiple" (string[]). Branch
  // the render so each side gets correctly-typed props.
  const useSingle = oneAtATime && !isEditing();

  const body = flatContent.map((item, index) => (
    <AccordionContext.Provider
      key={index}
      value={{
        shouldExpandDetails,
        itemIndex: index,
        arrowDirection,
        highlightExpandedItem,
      }}
    >
      <Blocks
        path={`component.options.items.${index}.content`}
        blocks={[item] as never}
        parent={builderBlock?.id}
      />
    </AccordionContext.Provider>
  ));

  if (useSingle) {
    return (
      <Accordion
        type="single"
        collapsible
        value={typeof expandedItems === "string" ? expandedItems : ""}
        onValueChange={value => setExpandedItems(value)}
        className={className}
        {...restAttributes}
        data-testid={dataTestId || "accordion-Accordion"}
      >
        {body}
      </Accordion>
    );
  }

  return (
    <Accordion
      type="multiple"
      value={Array.isArray(expandedItems) ? expandedItems : []}
      onValueChange={value => setExpandedItems(value)}
      className={className}
      {...restAttributes}
      data-testid={dataTestId || "accordion-Accordion"}
    >
      {body}
    </Accordion>
  );
};

export const AccordionDefinition = {
  component: BuilderAccordion,
  name: "Builder:Accordion",
  override: true,
  canHaveChildren: true,
  noWrap: true,
  childRequirements: {
    message: "Children of 'Accordion' must be 'AccordionItem'",
    query: {
      "component.name": { $in: ["AccordionItem"] },
    },
  },
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  inputs: [
    {
      name: "items",
      type: "uiBlocks",
      defaultValue: [{ content: [defaultItem] }],
    },
    {
      name: "oneAtATime",
      helperText:
        "Only allow opening one at a time (collapse all others when new item opened)",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "arrowDirection",
      type: "string",
      enum: ["right", "down"],
      defaultValue: "right",
      helperText: "Display arrow direction to bottom (default is to right)",
      friendlyName: "Arrow Direction",
    },
    {
      name: "highlightExpandedItem",
      friendlyName:
        "Highlight accordion item when expanded with background color",
      type: "boolean",
      defaultValue: false,
      helperText: "Background color will be applied only when item is opened",
    },
  ],
} satisfies RegisteredComponent;

// ---------------------------------------------------------------------------
// AccordionItem — wraps the HEC primitive AccordionItem, keyed by context index.
// ---------------------------------------------------------------------------

interface AccordionChildProps {
  content?: unknown[];
  builderBlock?: { id?: string };
  attributes?: BlockAttributes;
  "data-testid"?: string;
}

const BuilderAccordionItem = ({
  content = [],
  builderBlock,
  attributes,
  "data-testid": dataTestId,
}: AccordionChildProps) => {
  const { itemIndex } = useAccordionContext();
  const { className, restAttributes } = splitAttributes(attributes);

  return (
    <AccordionItem
      value={`item-${itemIndex}`}
      className={className}
      {...restAttributes}
      data-testid={dataTestId || "accordionItem-AccordionItem"}
    >
      <Blocks
        path="component.options.content"
        blocks={content as never}
        parent={builderBlock?.id}
      />
    </AccordionItem>
  );
};

export const AccordionItemDefinition = {
  component: BuilderAccordionItem,
  name: "AccordionItem",
  noWrap: true,
  canHaveChildren: true,
  childRequirements: {
    message:
      "Children of 'Accordion' must be 'AccordionTitle' then 'AccordionDetail'",
    query: {
      "component.name": { $in: ["AccordionItem", "AccordionDetail"] },
    },
  },
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  inputs: [
    {
      name: "content",
      type: "uiBlocks",
      broadcast: true,
      defaultValue: [defaultTitle, defaultDetail],
    },
  ],
} satisfies RegisteredComponent;

// ---------------------------------------------------------------------------
// AccordionTitle — wraps the HEC primitive AccordionTrigger.
// ---------------------------------------------------------------------------

const BuilderAccordionTitle = ({
  content = [],
  builderBlock,
  attributes,
  "data-testid": dataTestId,
}: AccordionChildProps) => {
  const { arrowDirection, highlightExpandedItem } = useAccordionContext();
  const { className, restAttributes } = splitAttributes(attributes);

  return (
    <AccordionTrigger
      className={className}
      arrowDirection={arrowDirection}
      highlightExpandedItem={highlightExpandedItem}
      {...restAttributes}
      data-testid={dataTestId || "accordionTitle-AccordionTrigger"}
    >
      <Blocks
        path="component.options.content"
        blocks={content as never}
        parent={builderBlock?.id}
      />
    </AccordionTrigger>
  );
};

export const AccordionTitleDefinition = {
  component: BuilderAccordionTitle,
  name: "AccordionTitle",
  noWrap: true,
  canHaveChildren: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  inputs: [
    {
      name: "content",
      type: "uiBlocks",
      broadcast: true,
      defaultValue: [defaultTitle],
    },
  ],
} satisfies RegisteredComponent;

// ---------------------------------------------------------------------------
// AccordionDetail — wraps the HEC primitive AccordionContent. Detail content is
// only mounted when the item is expanded (matches composable behavior).
// ---------------------------------------------------------------------------

const BuilderAccordionDetail = ({
  content = [],
  builderBlock,
  attributes,
  "data-testid": dataTestId,
}: AccordionChildProps) => {
  const { shouldExpandDetails, itemIndex } = useAccordionContext();
  const { className, restAttributes } = splitAttributes(attributes);

  const renderContent: ReactNode = shouldExpandDetails(itemIndex) ? (
    <Blocks
      path="component.options.content"
      blocks={content as never}
      parent={builderBlock?.id}
    />
  ) : null;

  return (
    <AccordionContent
      className={className}
      {...restAttributes}
      data-testid={dataTestId || "accordionDetail-AccordionContent"}
    >
      {renderContent}
    </AccordionContent>
  );
};

export const AccordionDetailDefinition = {
  component: BuilderAccordionDetail,
  name: "AccordionDetail",
  noWrap: true,
  canHaveChildren: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  inputs: [
    {
      name: "content",
      type: "uiBlocks",
      broadcast: true,
      defaultValue: [defaultDetail],
    },
  ],
} satisfies RegisteredComponent;
